import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeState {
  mode: ThemeMode;
  isDark: boolean;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}

const getSystemPreference = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

const applyTheme = (isDark: boolean) => {
  if (typeof document === 'undefined') return;

  if (isDark) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
};

export const useTheme = create<ThemeState>()(
  persist(
    (set, get) => {
      if (typeof window !== 'undefined') {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = () => {
          const state = get();
          if (state.mode === 'system') {
            const isDark = getSystemPreference();
            set({ isDark });
            applyTheme(isDark);
          }
        };

        if (mediaQuery.addEventListener) {
          mediaQuery.addEventListener('change', handleChange);
        }
      }

      const calculateIsDark = (mode: ThemeMode): boolean => {
        switch (mode) {
          case 'dark':
            return true;
          case 'light':
            return false;
          case 'system':
            return getSystemPreference();
          default:
            return false;
        }
      };

      return {
        mode: 'system',
        isDark: calculateIsDark('system'),
        setMode: (mode: ThemeMode) => {
          const isDark = calculateIsDark(mode);
          set({ mode, isDark });
          applyTheme(isDark);
        },
        toggleMode: () => {
          const { mode } = get();
          const newMode = mode === 'dark' ? 'light' : 'dark';
          const isDark = calculateIsDark(newMode);
          set({ mode: newMode, isDark });
          applyTheme(isDark);
        },
      };
    },
    {
      name: 'theme-storage',
      onRehydrateStorage: () => (state) => {
        if (state) {
          applyTheme(state.isDark);
        }
      },
    },
  ),
);

// Initialize theme on module load
if (typeof window !== 'undefined') {
  const state = useTheme.getState();
  applyTheme(state.isDark);
}
